/**
 * Created by noOne on 08/09/2017.
 */

/**
 * Class representing an A* search algorithm.  Pass in a matrix of objects, that has a pos property container the x, y values, as well as blocked properly (bool)
 * @class
 */
export default class AStar {
    /**
     * @param grid @type {Matrix}
     */
    constructor(grid) {

        /**
         * @type {Matrix<Object>}
         * @private
         */
        this._grid = grid;

        //--initialise the grid--//
        AStar.init(this._grid);
    }

    search(start, end) {

        const openList = [];
        const closedList = [];
        const grid = this._grid;

        if (grid[start.pos.x] === undefined) {
            console.warn("Pathfind warning : Found undefined grid square   x : " + start.pos.x + ", y : " + start.pos.y + "  (probably floating points in indices)");
            return [];
        } else if (grid[start.pos.x][start.pos.y] === undefined) {
            console.warn("Pathfind warning : Found undefined grid square   x : " + start.pos.x + ", y : " + start.pos.y + "  (probably floating points in indices)");
            return [];
        }

        openList.push(grid[start.pos.x][start.pos.y]);

        if (openList[0] === undefined) {
            console.warn("Pathfind warning : The first element in the open list is undefined   (probably floating points in indices)");
            return [];
        }

        while (openList.length > 0) {

            let lowInd = 0;
            for (let i = 0; i < openList.length; i++) {
                if (openList[i].f < openList[lowInd].f) {
                    lowInd = i;
                }
            }

            const currentNode = openList[lowInd];
            if (currentNode.pos.x == end.pos.x && currentNode.pos.y == end.pos.y) {
                let curr = currentNode;
                const ret = [];
                while (curr.parent) {
                    ret.push(curr);
                    curr = curr.parent;
                }
                //console.log("path found :\n" + JSON.stringify(ret));
                return ret;
            }

            //--move currentNode from open to closed--//
            let index = openList.indexOf(currentNode);
            openList.splice(index, 1);
            closedList.push(currentNode);

            const neighbours = AStar.neighbours(grid, currentNode);

            for (let i = 0; i < neighbours.length; i++) {
                const neighbour = neighbours[i];

                index = closedList.indexOf(neighbour);
                if (index !== -1 || neighbour.blocked) {//skip nodes if (condition)
                    continue; //skip node
                }

                let gScore = currentNode.g + 1; // 1 is the distance from a node to it's neighbour
                let gScoreIsBest = false;

                index = openList.indexOf(neighbour);

                if (index === -1) {
                    gScoreIsBest = true;
                    // neighbour.h = AStar.manhattan(neighbour.pos, end.pos);
                    neighbour.h = AStar.euclidean(neighbour.pos, end.pos);
                    openList.push(neighbour);
                }
                else if (gScore < neighbour.g) {
                    gScoreIsBest = true;
                }

                if (gScoreIsBest) {
                    neighbour.parent = currentNode;
                    neighbour.g = gScore;
                    neighbour.f = neighbour.g + neighbour.h;
                    // neighbour.debug = "F: " + neighbour.f + "     G: " + neighbour.g + "     H: " + neighbour.h;
                    // console.log(neighbour.debug);
                }
            }
        }
    }

    //============================//
    //======STATIC FUNCTIONS======//
    //============================//

    static init(grid) {
        for (let x = 0; x < grid.length; x++) {
            for (let y = 0; y < grid[x].length; y++) {
                grid[x][y].f = 0;
                grid[x][y].g = 0;
                grid[x][y].h = 0;
                //   grid[x][y].debug = "";
                grid[x][y].parent = null;
            }
        }
    }

    /**
     * Finds neighbouring nodes
     * @param grid
     * @param node
     * @returns {Array}
     */
    static neighbours(grid, node) {
        const neigh = [];

        const x = node.pos.x;
        const y = node.pos.y;

        if (grid[x - 1] && grid[x - 1][y]) {
            neigh.push(grid[x - 1][y]);
        }
        if (grid[x + 1] && grid[x + 1][y]) {
            neigh.push(grid[x + 1][y]);
        }
        if (grid[x][y - 1] && grid[x][y - 1]) {
            neigh.push(grid[x][y - 1]);
        }
        if (grid[x][y + 1] && grid[x][y + 1]) {
            neigh.push(grid[x][y + 1]);
        }
        //below does the same but also includes nodes at diagonals
        /*
         if (grid[x - 1] && grid[x - 1][y]) {
         neigh.push(grid[x - 1][y]);
         }
         if (grid[x - 1] && grid[x - 1][y - 1]) {
         neigh.push(grid[x - 1][y - 1]);
         }
         if (grid[x + 1] && grid[x + 1][y]) {
         neigh.push(grid[x + 1][y]);
         }
         if (grid[x + 1] && grid[x + 1][y + 1]) {
         neigh.push(grid[x + 1][y + 1]);
         }
         if (grid[x][y - 1] && grid[x][y - 1]) {
         neigh.push(grid[x][y - 1]);
         }
         if (grid[x - 1][y + 1] && grid[x - 1][y + 1]) {
         neigh.push(grid[x - 1][y + 1]);
         }
         if (grid[x][y + 1] && grid[x][y + 1]) {
         neigh.push(grid[x][y + 1]);
         }
         if (grid[x + 1][y - 1] && grid[x + 1][y - 1]) {
         neigh.push(grid[x + 1][y - 1]);
         }
         */
        return neigh;
    }

    /**
     * Calculates manhattan distance to use as a distance heuristic
     * @param pos0
     * @param pos1
     * @returns {*}
     */
    static manhattan(pos0, pos1) {
        const dx = Math.abs(pos1.x - pos0.x);
        const dy = Math.abs(pos1.y - pos0.y);
        return dx + dy;
    }

    /**
     * Use euclidean distance as your heuristic if you want to path in any direction (not just cardinal directions)
     * @param pos0
     * @param pos1
     * @returns {number|*}
     */
    static euclidean(pos0, pos1) {
        const dx = Math.abs(pos0.x - pos1.x);
        const dy = Math.abs(pos0.y - pos1.y);
        return Math.sqrt(dx * dx + dy * dy);
    }
}
